import { ReactNode, useContext, useEffect, useState } from 'react';
import { toast } from '../../util/toasts';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { Card } from '../../components/card/card';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Typography } from '../../components/typography/typography';
import { TenantContext } from '../../context/tenant-context';
import { useVendorApi } from '../../clients/quipli-service/vendor/vendor-api';
import { Vendor } from '../../clients/quipli-service/vendor/vendor';
import { VendorForm } from './vendor-form';
import { UserAddress } from '../../clients/quipli-service/user/user';
import SkeletonLoader from '../../components/skeleton-loader/skeleton-loader';
import Icon from '../../components/icons/icon';
import { Button } from '../../components/form/button';
import { UserAddressForm } from '../customer/components/user-address-form';
import { Dropdown } from '../../components/dropdown/dropdown';
import { formatAddress, formatPhoneNumber } from '../../util/util';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { RouteTabs } from '../../components/tab-nav-v2/route-tabs';

type VendorViewContext = { vendor?: Vendor; isLoading: boolean; fetchVendor: () => Promise<void> };

export const VendorView = () => {
  const { vendorId } = useParams();
  const { getVendor } = useVendorApi();
  const { contextPath } = useContext(TenantContext);
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState<boolean>(true);
  const [vendor, setVendor] = useState<Vendor>();

  const handleBack = () => {
    navigate(`${contextPath}manager/vendors`);
  };

  const fetchVendor = async () => {
    if (!vendorId) {
      return;
    }

    try {
      const vendor = await getVendor(vendorId);
      setVendor(vendor);
    } catch (e: any) {
      toast(e.message.msg ?? e.message ?? 'Failed to fetch vendor', 'error');
      handleBack();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!vendorId) {
      handleBack();
      toast('Invalid vendor id', 'error');
    }

    fetchVendor();
  }, []);

  return (
    <div className="px-[10px] pb-[5px] flex flex-col gap-5">
      <div className="flex">
        <Button className="button-tertiary !pl-0" onClick={() => navigate(-1)}>
          <Icon icon="Back" size="large" />
        </Button>

        <Typography type="display-sm" weight="medium">
          Vendor Setup
        </Typography>
      </div>

      <Card className="!px-0 md:max-w-xl">
        <RouteTabs tabNames={['General', 'Address']} path="/vendors/:vendorId" />
        <div className="p-4">
          <Outlet context={{ vendor, isLoading, fetchVendor }} />
        </div>
      </Card>
    </div>
  );
};

export const VendorGeneralForm = () => {
  const { vendor, isLoading, fetchVendor } = useOutletContext<VendorViewContext>();

  return !vendor && isLoading ? (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={`vendor-skeleton-${i}`} className="flex flex-col gap-[6px]">
          <SkeletonLoader style={{ width: '33%', height: '20px' }} />
          <SkeletonLoader style={{ width: '100%', height: '40px' }} />
        </div>
      ))}

      <div className="flex justify-end">
        <SkeletonLoader style={{ width: '60px', height: '33px' }} />
      </div>
    </div>
  ) : (
    <VendorForm
      vendor={vendor}
      onSubmit={async () => {
        fetchVendor();
        toast('Vendor updated successfully', 'success');
      }}
    />
  );
};

export const VendorAddressForm = () => {
  const { vendor } = useOutletContext<VendorViewContext>();
  const { getUserAddresses, deleteUserAddress } = useAPI();

  const [isLoading, setLoading] = useState<boolean>(true);
  const [address, setAddress] = useState<UserAddress>();
  const [modal, setModal] = useState<ReactNode | undefined>();

  const fetchAddress = async () => {
    if (!vendor) {
      return;
    }

    setLoading(true);

    try {
      const addresses = await getUserAddresses(vendor.id);
      setAddress(addresses[0] ?? undefined);
    } catch (e: any) {
      toast(e.message.msg ?? e.message ?? 'Failed to fetch address', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddress();
  }, [vendor]);

  return (
    <div>
      {isLoading ? (
        <div className="flex flex-col gap-3.5">
          <div className="flex justify-between items-center">
            <SkeletonLoader style={{ height: '32px', width: '100px' }} />
            <SkeletonLoader style={{ height: '32px', width: '32px' }} />
          </div>

          <SkeletonLoader style={{ height: '20px', width: '100%' }} />
          <SkeletonLoader style={{ height: '20px', width: '60%' }} />
        </div>
      ) : (
        <>
          {!address ? (
            <div className="flex flex-col py-3.5 items-center justify-center gap-3.5">
              <Icon icon="Location02" size="large" />
              <Typography type="text-sm" weight="medium" className="property text-grey-500 text-center">
                Looks like you haven't added an address yet.
                <br /> Click “Add Address” to get started
              </Typography>
              <Button
                className="button-yellow"
                onClick={() =>
                  setModal(
                    <UserAddressForm
                      isOpen
                      hideAddressType
                      hideSaveForFutureUse
                      title="Vendor Address Setup"
                      onSubmit={async () => {
                        await fetchAddress();
                        setModal(undefined);
                      }}
                      onClose={() => setModal(undefined)}
                      userAddress={{
                        id: 0,
                        name: '',
                        userId: vendor!.id,
                        address1: '',
                        address2: '',
                        country: '',
                        type: 'delivery',
                        city: '',
                        state: '',
                        zip: '',
                        isGoogleGenerated: false,
                        isDeleted: false,
                        isDefault: false,
                      }}
                    />,
                  )
                }
              >
                Add Address
              </Button>
            </div>
          ) : (
            <div className="flex flex-column gap-4">
              <div className="flex items-center justify-between">
                <Typography type="text-sm" weight="medium" className="text-quipli-black-800">
                  {address.name}
                </Typography>

                <div>
                  <Dropdown
                    trigger={() => (
                      <Button className="button-tertiary">
                        <Icon icon="DotsVertical" stroke="var(--grey-800)" size="small" />
                      </Button>
                    )}
                  >
                    <div>
                      <div
                        className="flex gap-2"
                        onClick={() =>
                          setModal(
                            <UserAddressForm
                              isOpen
                              hideAddressType
                              hideSaveForFutureUse
                              title="Vendor Address Setup"
                              onSubmit={async () => {
                                fetchAddress();
                                setModal(undefined);
                              }}
                              onClose={() => setModal(undefined)}
                              userAddress={address}
                            />,
                          )
                        }
                        data-cy="disable_customer"
                      >
                        <Typography type="text-sm" className="text-quipli-black-800">
                          Edit Address
                        </Typography>
                      </div>
                    </div>
                    <div>
                      <div
                        className="flex gap-2"
                        onClick={() =>
                          setModal(
                            <ConfirmModal
                              isOpen
                              close={() => setModal(undefined)}
                              onConfirm={async () => {
                                await deleteUserAddress(address.id);
                                fetchAddress();
                                setModal(undefined);
                              }}
                              headerIcon={
                                <div className="flex justify-center">
                                  <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                                </div>
                              }
                              prompt="Remove item?"
                              additionalText="This cannot be undone"
                              noLabel="Cancel"
                              yesLabel="Remove"
                            />,
                          )
                        }
                        data-cy="delete_customer"
                      >
                        <Typography type="text-sm" className="text-error-500">
                          Delete address
                        </Typography>
                      </div>
                    </div>
                  </Dropdown>
                </div>
              </div>
              <span className="quipli-v2 text-sm regular text-[var(--quipli-black-800)]">{formatAddress(address)}</span>
              <span className="quipli-v2 text-sm regular text-[var(--quipli-black-800)]">
                {formatPhoneNumber(address.phone)}
              </span>
            </div>
          )}

          {modal}
        </>
      )}
    </div>
  );
};
